import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Loader2, Check, AlertCircle, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EditableField = ({ label, value, onChange, type = "text", rows = 1, placeholder = "" }) => {
  if (type === "textarea" || rows > 1) {
    return (
      <div className="mb-4">
        <label className="block font-oswald text-sm uppercase tracking-wider text-brand-lime mb-2">
          {label}
        </label>
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          className="w-full bg-brand-black border border-white/20 text-white px-4 py-3 font-montserrat focus:border-brand-lime focus:outline-none transition-colors resize-none"
        />
      </div>
    );
  }
  
  return (
    <div className="mb-4">
      <label className="block font-oswald text-sm uppercase tracking-wider text-brand-lime mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-brand-black border border-white/20 text-white px-4 py-3 font-montserrat focus:border-brand-lime focus:outline-none transition-colors"
      />
    </div>
  );
};

const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-brand-lime/20 mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-brand-dark hover:bg-brand-dark/80 transition-colors"
      >
        <span className="font-oswald text-lg uppercase tracking-wider text-white">{title}</span>
        {isOpen ? <ChevronUp className="text-brand-lime" /> : <ChevronDown className="text-brand-lime" />}
      </button>
      {isOpen && (
        <div className="p-6 bg-brand-black">
          {children}
        </div>
      )}
    </div>
  );
};

export const ContentEditorPage = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success', 'error', null
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchContent();
  }, [navigate]);

  const fetchContent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/content/accountability-chats`);
      if (response.ok) {
        const data = await response.json();
        if (data && data.content) {
          setContent(data.content);
        } else {
          // Initialize with default structure
          setContent(getDefaultContent());
        }
      } else {
        setContent(getDefaultContent());
      }
    } catch (err) {
      setError('Failed to load content');
      setContent(getDefaultContent());
    } finally {
      setIsLoading(false);
    }
  };

  const getDefaultContent = () => ({
    hero: {
      title: "ACCOUNTABILITY",
      titleAccent: "CHATS",
      tagline: "Discipline. Structure.",
      taglineAccent: "Results.",
      description: "This is where the real change happens.",
      ctaText: "View Current Program"
    },
    features: {
      sectionLabel: "What's Included",
      sectionTitle: "Your",
      sectionTitleAccent: "Transformation",
      sectionTitleSuffix: "System",
      items: [{ text: "Daily accountability check-ins" }]
    },
    chooseYou: {
      badge: "NOW ENROLLING",
      title: "CHOOSE YOU.",
      titleAccent: "A 30-DAY SERIES",
      startDate: "Starts March 2",
      duration: "30 Days",
      descriptionIntro: "Choose You is a 30-day accountability mastermind reset.",
      descriptionPoints: ["This is for the person who keeps saying \"I'll start Monday.\""],
      descriptionOutro: "This program is designed to teach you how to keep your word with yourself.",
      motto: "No excuses. No hiding. No negotiating.",
      values: "Just ownership. Consistency. Growth."
    },
    pricing: {
      chatOnly: { price: "$100", title: "Chat Only", description: "For those already committed to a gym", buttonText: "Apply for Choose You" },
      chatWorkouts: { price: "$169", title: "Chat + Unlimited Workouts", description: "For those not currently committed to a gym", buttonText: "Apply for Choose You" }
    },
    payment: {
      title: "Payment Methods",
      cashApp: "$PowerUpYourFitness",
      zelle: "PowerUpYourFitness@gmail.com",
      proofNote: "Proof of payment must be emailed to PowerUpYourFitness@gmail.com"
    },
    upcoming: {
      sectionLabel: "Upcoming Accountability Chats (Enrollment Closed)",
      title: "Future Programs",
      description: "Stay tuned for upcoming accountability chat dates.",
      programs: [{ name: "21-Day Reset", status: "Coming Soon" }]
    },
    cta: {
      title: "Choose",
      titleAccent: "You",
      description: "Your transformation starts with accountability.",
      motto: "Ownership. Consistency.",
      mottoAccent: "Growth.",
      buttonText: "Apply for Choose You"
    },
    form: {
      title: "Apply for",
      titleAccent: "Choose You",
      subtitle: "30-Day Series - Starts March 2",
      submitText: "Apply for Choose You",
      confirmationTitle: "Application",
      confirmationTitleAccent: "Received!",
      confirmationMessage: "Your spot is reserved pending payment."
    }
  });

  const updateContent = (path, value) => {
    setContent(prev => {
      const newContent = { ...prev };
      const keys = path.split('.');
      let current = newContent;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (keys[i].includes('[')) {
          const [key, indexStr] = keys[i].split('[');
          const index = parseInt(indexStr.replace(']', ''));
          current = current[key][index];
        } else {
          current = current[keys[i]];
        }
      }
      
      const lastKey = keys[keys.length - 1];
      if (lastKey.includes('[')) {
        const [key, indexStr] = lastKey.split('[');
        const index = parseInt(indexStr.replace(']', ''));
        current[key][index] = value;
      } else {
        current[lastKey] = value;
      }
      
      return newContent;
    });
    setSaveStatus(null);
  };

  const addArrayItem = (path, defaultItem) => {
    setContent(prev => {
      const newContent = { ...prev };
      const keys = path.split('.');
      let current = newContent;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = [...current[keys[keys.length - 1]], defaultItem];
      return newContent;
    });
  };

  const removeArrayItem = (path, index) => {
    setContent(prev => {
      const newContent = { ...prev };
      const keys = path.split('.');
      let current = newContent;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = current[keys[keys.length - 1]].filter((_, i) => i !== index);
      return newContent;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus(null);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/content/accountability-chats`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(content)
      });
      
      if (response.ok) {
        setSaveStatus('success');
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        setSaveStatus('error');
      }
    } catch (err) {
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-black flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-brand-lime animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-black pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 text-brand-gray hover:text-white transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className="font-oswald text-3xl uppercase text-white">
                Edit <span className="text-brand-lime">Accountability Chats</span>
              </h1>
              <p className="font-montserrat text-sm text-brand-gray">
                Edit all text content on this page
              </p>
            </div>
          </div>
          
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 bg-brand-lime text-brand-black px-6 py-3 font-oswald uppercase tracking-wider hover:bg-brand-pink hover:text-white transition-all disabled:opacity-50"
          >
            {isSaving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : saveStatus === 'success' ? (
              <Check className="w-5 h-5" />
            ) : saveStatus === 'error' ? (
              <AlertCircle className="w-5 h-5" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            {isSaving ? 'Saving...' : saveStatus === 'success' ? 'Saved!' : saveStatus === 'error' ? 'Error' : 'Save Changes'}
          </button>
        </div>

        {/* Save Status Message */}
        {saveStatus && (
          <div className={`mb-6 p-4 border ${saveStatus === 'success' ? 'border-brand-lime/30 bg-brand-lime/10' : 'border-red-500/30 bg-red-500/10'}`}>
            <p className={`font-montserrat ${saveStatus === 'success' ? 'text-brand-lime' : 'text-red-400'}`}>
              {saveStatus === 'success' ? 'Content saved successfully!' : 'Failed to save content. Please try again.'}
            </p>
          </div>
        )}

        {/* Hero Section */}
        <CollapsibleSection title="Hero Section" defaultOpen={true}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EditableField 
              label="Title" 
              value={content?.hero?.title} 
              onChange={(val) => updateContent('hero.title', val)} 
            />
            <EditableField 
              label="Title Accent (Green)" 
              value={content?.hero?.titleAccent} 
              onChange={(val) => updateContent('hero.titleAccent', val)} 
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EditableField 
              label="Tagline" 
              value={content?.hero?.tagline} 
              onChange={(val) => updateContent('hero.tagline', val)} 
            />
            <EditableField 
              label="Tagline Accent (Green)" 
              value={content?.hero?.taglineAccent} 
              onChange={(val) => updateContent('hero.taglineAccent', val)} 
            />
          </div>
          <EditableField 
            label="Description" 
            value={content?.hero?.description} 
            onChange={(val) => updateContent('hero.description', val)} 
            rows={3}
          />
          <EditableField 
            label="CTA Button Text" 
            value={content?.hero?.ctaText} 
            onChange={(val) => updateContent('hero.ctaText', val)} 
          />
        </CollapsibleSection>

        {/* Features Section */}
        <CollapsibleSection title="What's Included Section">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <EditableField 
              label="Section Label" 
              value={content?.features?.sectionLabel} 
              onChange={(val) => updateContent('features.sectionLabel', val)} 
            />
            <EditableField 
              label="Title" 
              value={content?.features?.sectionTitle} 
              onChange={(val) => updateContent('features.sectionTitle', val)} 
            />
            <EditableField 
              label="Title Accent" 
              value={content?.features?.sectionTitleAccent} 
              onChange={(val) => updateContent('features.sectionTitleAccent', val)} 
            />
          </div>
          
          <div className="mt-6">
            <label className="block font-oswald text-sm uppercase tracking-wider text-brand-lime mb-4">
              Feature Items
            </label>
            {content?.features?.items?.map((item, index) => (
              <div key={index} className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  value={item.text}
                  onChange={(e) => {
                    const newItems = [...content.features.items];
                    newItems[index] = { text: e.target.value };
                    updateContent('features.items', newItems);
                  }}
                  className="flex-1 bg-brand-black border border-white/20 text-white px-4 py-2 font-montserrat focus:border-brand-lime focus:outline-none"
                />
                <button
                  onClick={() => removeArrayItem('features.items', index)}
                  className="p-2 text-red-400 hover:text-red-300"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
            <button
              onClick={() => addArrayItem('features.items', { text: 'New feature' })}
              className="flex items-center gap-2 text-brand-lime hover:text-brand-pink transition-colors mt-2"
            >
              <Plus size={18} /> Add Feature
            </button>
          </div>
        </CollapsibleSection>

        {/* Choose You Section */}
        <CollapsibleSection title="Choose You Program">
          <EditableField 
            label="Badge Text" 
            value={content?.chooseYou?.badge} 
            onChange={(val) => updateContent('chooseYou.badge', val)} 
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EditableField 
              label="Title" 
              value={content?.chooseYou?.title} 
              onChange={(val) => updateContent('chooseYou.title', val)} 
            />
            <EditableField 
              label="Title Accent (Green)" 
              value={content?.chooseYou?.titleAccent} 
              onChange={(val) => updateContent('chooseYou.titleAccent', val)} 
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EditableField 
              label="Start Date" 
              value={content?.chooseYou?.startDate} 
              onChange={(val) => updateContent('chooseYou.startDate', val)} 
            />
            <EditableField 
              label="Duration" 
              value={content?.chooseYou?.duration} 
              onChange={(val) => updateContent('chooseYou.duration', val)} 
            />
          </div>
          <EditableField 
            label="Description Intro" 
            value={content?.chooseYou?.descriptionIntro} 
            onChange={(val) => updateContent('chooseYou.descriptionIntro', val)} 
            rows={2}
          />
          
          <div className="mt-4">
            <label className="block font-oswald text-sm uppercase tracking-wider text-brand-lime mb-4">
              Description Points
            </label>
            {content?.chooseYou?.descriptionPoints?.map((point, index) => (
              <div key={index} className="flex items-start gap-2 mb-2">
                <textarea
                  value={point}
                  onChange={(e) => {
                    const newPoints = [...content.chooseYou.descriptionPoints];
                    newPoints[index] = e.target.value;
                    updateContent('chooseYou.descriptionPoints', newPoints);
                  }}
                  rows={2}
                  className="flex-1 bg-brand-black border border-white/20 text-white px-4 py-2 font-montserrat focus:border-brand-lime focus:outline-none resize-none"
                />
                <button
                  onClick={() => removeArrayItem('chooseYou.descriptionPoints', index)}
                  className="p-2 text-red-400 hover:text-red-300"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
            <button
              onClick={() => addArrayItem('chooseYou.descriptionPoints', 'New point')}
              className="flex items-center gap-2 text-brand-lime hover:text-brand-pink transition-colors mt-2"
            >
              <Plus size={18} /> Add Point
            </button>
          </div>
          
          <EditableField 
            label="Description Outro" 
            value={content?.chooseYou?.descriptionOutro} 
            onChange={(val) => updateContent('chooseYou.descriptionOutro', val)} 
            rows={2}
          />
          <EditableField 
            label="Motto" 
            value={content?.chooseYou?.motto} 
            onChange={(val) => updateContent('chooseYou.motto', val)} 
          />
          <EditableField 
            label="Values" 
            value={content?.chooseYou?.values} 
            onChange={(val) => updateContent('chooseYou.values', val)} 
          />
        </CollapsibleSection>

        {/* Pricing Section */}
        <CollapsibleSection title="Pricing">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="border border-brand-lime/20 p-4">
              <h4 className="font-oswald text-lg text-brand-lime mb-4">Chat Only Option</h4>
              <EditableField 
                label="Price" 
                value={content?.pricing?.chatOnly?.price} 
                onChange={(val) => updateContent('pricing.chatOnly.price', val)} 
              />
              <EditableField 
                label="Title" 
                value={content?.pricing?.chatOnly?.title} 
                onChange={(val) => updateContent('pricing.chatOnly.title', val)} 
              />
              <EditableField 
                label="Description" 
                value={content?.pricing?.chatOnly?.description} 
                onChange={(val) => updateContent('pricing.chatOnly.description', val)} 
              />
              <EditableField 
                label="Button Text" 
                value={content?.pricing?.chatOnly?.buttonText} 
                onChange={(val) => updateContent('pricing.chatOnly.buttonText', val)} 
              />
            </div>
            
            <div className="border border-brand-lime/20 p-4">
              <h4 className="font-oswald text-lg text-brand-lime mb-4">Chat + Workouts Option</h4>
              <EditableField 
                label="Price" 
                value={content?.pricing?.chatWorkouts?.price} 
                onChange={(val) => updateContent('pricing.chatWorkouts.price', val)} 
              />
              <EditableField 
                label="Title" 
                value={content?.pricing?.chatWorkouts?.title} 
                onChange={(val) => updateContent('pricing.chatWorkouts.title', val)} 
              />
              <EditableField 
                label="Description" 
                value={content?.pricing?.chatWorkouts?.description} 
                onChange={(val) => updateContent('pricing.chatWorkouts.description', val)} 
              />
              <EditableField 
                label="Button Text" 
                value={content?.pricing?.chatWorkouts?.buttonText} 
                onChange={(val) => updateContent('pricing.chatWorkouts.buttonText', val)} 
              />
            </div>
          </div>
        </CollapsibleSection>

        {/* Payment Section */}
        <CollapsibleSection title="Payment Methods">
          <EditableField 
            label="Section Title" 
            value={content?.payment?.title} 
            onChange={(val) => updateContent('payment.title', val)} 
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EditableField 
              label="Cash App" 
              value={content?.payment?.cashApp} 
              onChange={(val) => updateContent('payment.cashApp', val)} 
            />
            <EditableField 
              label="Zelle" 
              value={content?.payment?.zelle} 
              onChange={(val) => updateContent('payment.zelle', val)} 
            />
          </div>
          <EditableField 
            label="Proof Note" 
            value={content?.payment?.proofNote} 
            onChange={(val) => updateContent('payment.proofNote', val)} 
            rows={2}
          />
        </CollapsibleSection>

        {/* Upcoming Section */}
        <CollapsibleSection title="Upcoming Programs">
          <EditableField 
            label="Section Label" 
            value={content?.upcoming?.sectionLabel} 
            onChange={(val) => updateContent('upcoming.sectionLabel', val)} 
          />
          <EditableField 
            label="Title" 
            value={content?.upcoming?.title} 
            onChange={(val) => updateContent('upcoming.title', val)} 
          />
          <EditableField 
            label="Description" 
            value={content?.upcoming?.description} 
            onChange={(val) => updateContent('upcoming.description', val)} 
            rows={2}
          />
          
          <div className="mt-4">
            <label className="block font-oswald text-sm uppercase tracking-wider text-brand-lime mb-4">
              Programs
            </label>
            {content?.upcoming?.programs?.map((program, index) => (
              <div key={index} className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  value={program.name}
                  onChange={(e) => {
                    const newPrograms = [...content.upcoming.programs];
                    newPrograms[index] = { ...program, name: e.target.value };
                    updateContent('upcoming.programs', newPrograms);
                  }}
                  placeholder="Program name"
                  className="flex-1 bg-brand-black border border-white/20 text-white px-4 py-2 font-montserrat focus:border-brand-lime focus:outline-none"
                />
                <input
                  type="text"
                  value={program.status}
                  onChange={(e) => {
                    const newPrograms = [...content.upcoming.programs];
                    newPrograms[index] = { ...program, status: e.target.value };
                    updateContent('upcoming.programs', newPrograms);
                  }}
                  placeholder="Status"
                  className="w-32 bg-brand-black border border-white/20 text-white px-4 py-2 font-montserrat focus:border-brand-lime focus:outline-none"
                />
                <button
                  onClick={() => removeArrayItem('upcoming.programs', index)}
                  className="p-2 text-red-400 hover:text-red-300"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
            <button
              onClick={() => addArrayItem('upcoming.programs', { name: 'New Program', status: 'Coming Soon' })}
              className="flex items-center gap-2 text-brand-lime hover:text-brand-pink transition-colors mt-2"
            >
              <Plus size={18} /> Add Program
            </button>
          </div>
        </CollapsibleSection>

        {/* CTA Section */}
        <CollapsibleSection title="Bottom CTA Section">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EditableField 
              label="Title" 
              value={content?.cta?.title} 
              onChange={(val) => updateContent('cta.title', val)} 
            />
            <EditableField 
              label="Title Accent (Green)" 
              value={content?.cta?.titleAccent} 
              onChange={(val) => updateContent('cta.titleAccent', val)} 
            />
          </div>
          <EditableField 
            label="Description" 
            value={content?.cta?.description} 
            onChange={(val) => updateContent('cta.description', val)} 
            rows={2}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EditableField 
              label="Motto" 
              value={content?.cta?.motto} 
              onChange={(val) => updateContent('cta.motto', val)} 
            />
            <EditableField 
              label="Motto Accent (Green)" 
              value={content?.cta?.mottoAccent} 
              onChange={(val) => updateContent('cta.mottoAccent', val)} 
            />
          </div>
          <EditableField 
            label="Button Text" 
            value={content?.cta?.buttonText} 
            onChange={(val) => updateContent('cta.buttonText', val)} 
          />
        </CollapsibleSection>

        {/* Form Section */}
        <CollapsibleSection title="Signup Form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EditableField 
              label="Form Title" 
              value={content?.form?.title} 
              onChange={(val) => updateContent('form.title', val)} 
            />
            <EditableField 
              label="Form Title Accent (Green)" 
              value={content?.form?.titleAccent} 
              onChange={(val) => updateContent('form.titleAccent', val)} 
            />
          </div>
          <EditableField 
            label="Subtitle" 
            value={content?.form?.subtitle} 
            onChange={(val) => updateContent('form.subtitle', val)} 
          />
          <EditableField 
            label="Submit Button Text" 
            value={content?.form?.submitText} 
            onChange={(val) => updateContent('form.submitText', val)} 
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EditableField 
              label="Confirmation Title" 
              value={content?.form?.confirmationTitle} 
              onChange={(val) => updateContent('form.confirmationTitle', val)} 
            />
            <EditableField 
              label="Confirmation Title Accent" 
              value={content?.form?.confirmationTitleAccent} 
              onChange={(val) => updateContent('form.confirmationTitleAccent', val)} 
            />
          </div>
          <EditableField 
            label="Confirmation Message" 
            value={content?.form?.confirmationMessage} 
            onChange={(val) => updateContent('form.confirmationMessage', val)} 
            rows={3}
          />
        </CollapsibleSection>

        {/* Save Button (bottom) */}
        <div className="mt-8 text-center">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center gap-2 bg-brand-lime text-brand-black px-10 py-4 font-oswald uppercase tracking-wider text-lg hover:bg-brand-pink hover:text-white transition-all disabled:opacity-50"
          >
            {isSaving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            {isSaving ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};
